<template>
  <b-modal
    id="app-recommendation-modal"
    ref="appRecommendationModal"
    v-model="showModal"
    title="Application ReFEREEs"
    size="lg"
    centered
    hide-footer
    no-close-on-backdrop
    no-close-on-esc
  >
    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-4">
      <b-spinner variant="primary" label="Loading reFEREEs..."></b-spinner>
      <p class="mt-2 text-muted">Loading referee details...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <b-icon icon="exclamation-triangle" class="mr-2"></b-icon>
      {{ error }}
    </div>

    <!-- No ReFEREEs -->
    <div v-else-if="!referees.length" class="text-center py-4">
      <b-icon icon="inbox" scale="2" class="text-muted mb-3"></b-icon>
      <h6 class="text-muted">No reFEREEs found</h6>
      <p class="text-muted">
        No referees have been assigned to this application yet.
      </p>
    </div>

    <!-- ReFEREEs List -->
    <div v-else>
      <!-- Application Info Header -->
      <div class="mb-4 p-3 bg-light rounded">
        <h6 class="mb-2">
          <b-icon icon="file-earmark-text" class="mr-2"></b-icon>
          Application ID: {{ applicationId }}
        </h6>
        <p class="mb-0 text-muted">
          {{ referees.length }} referee(s) assigned to this application
        </p>
      </div>

      <!-- Referees Cards -->
      <div class="referees-list">
        <b-card
          v-for="(referee, index) in referees"
          :key="referee.id || index"
          class="mb-3 referee-card"
          :class="{
            submitted: referee.is_submitted,
            pending: !referee.is_submitted,
          }"
        >
          <div class="d-flex justify-content-between align-items-start">
            <!-- Referee Details -->
            <div class="referee-info flex-grow-1">
              <div class="d-flex align-items-center mb-2">
                <b-avatar
                  size="sm"
                  :text="getInitials(referee.fullname)"
                  :variant="referee.is_submitted ? 'success' : 'secondary'"
                  class="mr-2"
                ></b-avatar>
                <div>
                  <h6 class="mb-0">
                    {{ referee.title || "Dr." }} {{ referee.fullname }}
                  </h6>
                  <small class="text-muted">{{
                    referee.position || "Position not specified"
                  }}</small>
                </div>
              </div>

              <!-- Submission Status -->
              <div class="submission-status mb-2">
                <b-badge
                  :variant="referee.is_submitted ? 'success' : 'warning'"
                  class="mr-2"
                >
                  <b-icon
                    :icon="referee.is_submitted ? 'check-circle' : 'clock'"
                    class="mr-1"
                  ></b-icon>
                  {{ referee.is_submitted ? "Submitted" : "Pending" }}
                </b-badge>

                <!-- Submission Date -->
                <small
                  v-if="referee.is_submitted && referee.submitted_at"
                  class="text-muted"
                >
                  <b-icon icon="calendar" class="mr-1"></b-icon>
                  Submitted on {{ formatDate(referee.submitted_at) }}
                </small>
              </div>

              <!-- Additional Info -->
              <div v-if="referee.email" class="contact-info">
                <small class="text-muted">
                  <b-icon icon="envelope" class="mr-1"></b-icon>
                  {{ referee.email }}
                </small>
              </div>
            </div>

            <!-- Action Button -->
            <div class="action-button ml-3">
              <!-- Download Button (if submitted) -->
              <button
                v-if="referee.is_submitted"
                @click="downloadRecommendation(referee)"
                class="btn btn-sm btn-outline-primary"
                :disabled="downloadingIds.includes(referee.id)"
                v-b-tooltip.hover
                title="Download Recommendation PDF"
              >
                <b-spinner
                  v-if="downloadingIds.includes(referee.id)"
                  small
                  class="mr-1"
                ></b-spinner>
                <b-icon v-else icon="download" class="mr-1"></b-icon>
                {{
                  downloadingIds.includes(referee.id)
                    ? "Downloading..."
                    : "Download"
                }}
              </button>

              <!-- Pending Icon (if not submitted) -->
              <div
                v-else
                class="text-center"
                v-b-tooltip.hover
                title="Recommendation not yet submitted"
              >
                <b-icon
                  icon="hourglass-split"
                  scale="1.5"
                  class="text-warning pending-icon"
                ></b-icon>
                <br />
                <small class="text-muted">Pending</small>
              </div>
            </div>
          </div>

          <!-- Progress Indicator -->
          <div v-if="referee.is_submitted" class="mt-2">
            <div class="progress-indicator">
              <div class="progress-step completed">
                <b-icon icon="person-plus" class="mr-1"></b-icon>
                Assigned
              </div>
              <div class="progress-step completed">
                <b-icon icon="envelope-open" class="mr-1"></b-icon>
                Contacted
              </div>
              <div class="progress-step completed">
                <b-icon icon="check-circle" class="mr-1"></b-icon>
                Submitted
              </div>
            </div>
          </div>
        </b-card>
      </div>

      <!-- Summary -->
      <div class="summary-section mt-4 p-3 bg-info bg-opacity-10 rounded">
        <div class="row text-center">
          <div class="col-4">
            <h5 class="mb-0 text-success">{{ submittedCount }}</h5>
            <small class="text-muted">Submitted</small>
          </div>
          <div class="col-4">
            <h5 class="mb-0 text-warning">{{ pendingCount }}</h5>
            <small class="text-muted">Pending</small>
          </div>
          <div class="col-4">
            <h5 class="mb-0 text-info">{{ referees.length }}</h5>
            <small class="text-muted">Total</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Footer -->
    <template #modal-footer="{ hide }">
      <div class="w-100 d-flex justify-content-between">
        <div>
          <!-- Refresh Button -->
          <button
            @click="loadReferees"
            class="btn btn-outline-secondary"
            :disabled="isLoading"
          >
            <b-icon icon="arrow-clockwise" class="mr-1"></b-icon>
            Refresh
          </button>
        </div>
        <div>
          <!-- Close Button -->
          <button class="btn btn-secondary" @click="hide()">
            Close
          </button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
export default {
  name: "AppRecommendationModal",
  props: {
    // API endpoints
    refereesEndpoint: {
      type: String,
      default: "api/applications/{id}/referees",
    },
    downloadEndpoint: {
      type: String,
      default: "api/applications/reFEREEs/{id}/download",
    },
  },
  data() {
    return {
      showModal: false,
      isLoading: false,
      error: null,
      applicationId: null,
      referees: [],
      downloadingIds: [], // Track which reFEREEs are being downloaded
    };
  },
  computed: {
    submittedCount() {
      return this.referees.filter((referee) => referee.is_submitted).length;
    },
    pendingCount() {
      return this.referees.filter((referee) => !referee.is_submitted).length;
    },
  },
  methods: {
    // Open modal and load referees
    openModal(applicationId) {
      this.applicationId = applicationId;
      this.showModal = true;
      this.loadReferees();
    },

    // Close modal
    closeModal() {
      this.showModal = false;
      this.resetData();
    },

    // Reset component data
    resetData() {
      this.applicationId = null;
      this.referees = [];
      this.error = null;
      this.isLoading = false;
      this.downloadingIds = [];
    },

    // Load referees for the application
    async loadReferees() {
      if (!this.applicationId) return;

      this.isLoading = true;
      this.error = null;

      try {
        const response = await this.$store.dispatch(
          "Application/getReferees",
          this.applicationId
        );

        if (response && response.success) {
          this.referees = response.data || [];
        } else {
          throw new Error(response.message || "Failed to load referees");
        }
      } catch (error) {
        console.error("Error loading referees:", error);
        this.error = error.message || "Failed to load referee details";
        this.referees = [];
      } finally {
        this.isLoading = false;
      }
    },

    // Download recommendation PDF
    async downloadRecommendation(referee) {
      if (!referee.is_submitted) {
        Toast.fire({
          icon: "warning",
          title: "Recommendation not yet submitted",
        });
        return;
      }

      // Add to downloading list
      this.downloadingIds.push(referee.id);

      try {
        const response = await this.$store.dispatch(
          "Application/downloadRecommendation",
          {
            applicationId: this.applicationId,
            refereeId: referee.id,
          }
        );

        // Create download link
        const blob = new Blob([response], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `recommendation_${referee.fullname.replace(
          /\s+/g,
          "_"
        )}_${this.applicationId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        Toast.fire({
          icon: "success",
          title: "Recommendation downloaded successfully!",
        });

        this.$emit("recommendation-downloaded", {
          applicationId: this.applicationId,
          referee: referee,
          timestamp: new Date(),
        });
      } catch (error) {
        console.error("Download error:", error);
        Toast.fire({
          icon: "error",
          title: error.message || "Failed to download recommendation",
        });
      } finally {
        // Remove from downloading list
        this.downloadingIds = this.downloadingIds.filter(
          (id) => id !== referee.id
        );
      }
    },

    // Get initials for avatar
    getInitials(fullname) {
      if (!fullname) return "N/A";
      return fullname
        .split(" ")
        .map((name) => name.charAt(0))
        .join("")
        .substring(0, 2)
        .toUpperCase();
    },

    // Format date
    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        return new Date(dateString).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        return dateString;
      }
    },
  },
  created() {
    // Listen for events
    Fire.$on("SHOW-REFEREES", (applicationData) => {
      if (typeof applicationData === "object" && applicationData.id) {
        this.openModal(applicationData.id);
      } else {
        this.openModal(applicationData); // If just ID is passed
      }
    });

    Fire.$on("LOAD-REFEREE-DETAILS", (data) => {
      this.openModal(data.applicationId);
    });
  },
  beforeDestroy() {
    // Fire.$off("SHOW-REFEREES");
    // Fire.$off("LOAD-REFEREE-DETAILS");
  },
};
</script>

<style scoped>
.referee-card {
  border-left: 4px solid #dee2e6;
  transition: all 0.3s ease;
}

.referee-card.submitted {
  border-left-color: #28a745;
  background-color: #f8fff8;
}

.referee-card.pending {
  border-left-color: #ffc107;
  background-color: #fffdf8;
}

.referee-info h6 {
  color: #495057;
  font-weight: 600;
}

.submission-status .badge {
  font-size: 0.75rem;
}

.pending-icon {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

.progress-indicator {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  margin-top: 1rem;
}

.progress-step {
  color: #28a745;
  font-weight: 500;
}

.progress-step.completed {
  color: #28a745;
}

.summary-section h5 {
  font-weight: bold;
}

.action-button {
  min-width: 100px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .referee-card .d-flex {
    flex-direction: column;
  }

  .action-button {
    margin-top: 1rem;
    margin-left: 0 !important;
    align-self: flex-start;
  }

  .progress-indicator {
    flex-direction: column;
    gap: 0.5rem;
  }
}

/* Loading and error states */
.alert {
  border: none;
  border-radius: 0.5rem;
}

/* Contact info styling */
.contact-info {
  margin-top: 0.5rem;
}

/* Background opacity utility */
.bg-opacity-10 {
  background-color: rgba(0, 123, 255, 0.1) !important;
}
</style>
